import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { elUpperCase } from '../utils/text';
import {
  Box, Typography, Button, Paper, Chip, LinearProgress, IconButton,
  List, ListItem, ListItemText, ListItemIcon, Divider, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RouteIcon from '@mui/icons-material/Route';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceIcon from '@mui/icons-material/Science';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import UndoIcon from '@mui/icons-material/Undo';
import GroupIcon from '@mui/icons-material/Group';
import { Map, Marker, Popup, Source, Layer } from '@vis.gl/react-maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';

function detectBrowser() {
  const ua = navigator.userAgent;
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/Edg\//i.test(ua)) return 'edge';
  if (/Chrome/i.test(ua)) return 'chrome';
  if (/Firefox/i.test(ua)) return 'firefox';
  if (/Safari/i.test(ua)) return /iPhone|iPad|iPod/i.test(ua) ? 'safari_ios' : 'safari_mac';
  return 'other';
}
const MIN_DISTANCE_M = 20;
const STOP_STATUS_POLL_INTERVAL = 20000;

function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371e3;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';

// Two short beeps via the Web Audio API — no audio asset needed. The AudioContext
// is created lazily (and reused) so it lands within a user-gesture-initiated
// context, avoiding autoplay-blocking on mobile browsers.
function playProximityAlertSound(audioCtxRef) {
  try {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtxRef.current = new Ctx();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();
    [0, 0.18].forEach((delay) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.16);
    });
  } catch {
    // Ignore audio failures — the visual popup still shows.
  }
}

// Must run synchronously inside a real tap handler (Start/Resume/Simulate route),
// before any `await`. The proximity alert itself fires later from a GPS-position
// effect, which is not a user gesture — on iOS/WebKit an AudioContext created or
// resumed there stays silently suspended. Creating (and resuming) it here, inside
// the gesture, keeps it unlocked for the later programmatic playProximityAlertSound() calls.
function unlockProximityAlertAudio(audioCtxRef) {
  try {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtxRef.current = new Ctx();
    }
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
  } catch {
    // Ignore — playProximityAlertSound will just no-op later.
  }
}

// Fallback for browsers with no Wake Lock API (e.g. iOS/WebKit before 16.4 — this
// covers Chrome on iOS too, since it runs on the same engine) or where a native
// request silently fails (iOS Low Power Mode can reject it). A looping video keeps
// the screen awake the same way real video playback does, and muted-video autoplay
// is exempt from mobile autoplay-gesture restrictions, so this works without a tap.
// Backed by a canvas stream — no media asset needed. Kept genuinely (if barely)
// visible and pinned inside the viewport rather than opacity:0/off-screen: recent
// iOS versions specifically detect and pause "fake" fully-hidden keep-awake videos,
// and a perfectly static frame can also get elided as a no-op stream, so the canvas
// is redrawn with an alternating pixel to look like real, live video content.
function ensureNoSleepVideo(videoRef) {
  if (videoRef.current) return videoRef.current;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 2;
    canvas.height = 2;
    const ctx2d = canvas.getContext('2d');
    if (!canvas.captureStream) return null;
    let toggle = false;
    const draw = () => {
      toggle = !toggle;
      ctx2d.fillStyle = toggle ? '#000' : '#fff';
      ctx2d.fillRect(0, 0, 2, 2);
    };
    draw();
    const drawIntervalId = setInterval(draw, 1000);

    const video = document.createElement('video');
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.loop = true;
    video.style.cssText = 'position:fixed;bottom:0;right:0;width:2px;height:2px;opacity:0.01;pointer-events:none;z-index:-1;';
    video.srcObject = canvas.captureStream(2);
    video.addEventListener('pause', () => {
      if (document.visibilityState === 'visible') video.play().catch(() => {});
    });
    document.body.appendChild(video);
    video._drawIntervalId = drawIntervalId;
    videoRef.current = video;
    return video;
  } catch {
    return null;
  }
}

function releaseNoSleepVideo(videoRef) {
  const video = videoRef.current;
  if (!video) return;
  try {
    clearInterval(video._drawIntervalId);
    video.pause();
    video.srcObject?.getTracks().forEach((t) => t.stop());
    video.remove();
  } catch {}
  videoRef.current = null;
}

const GREEN = '#10b981';
const ORANGE = '#f59e0b';
const BLUE = '#3b82f6';
const GRAY = '#94a3b8';
const RED = '#ef4444';

function stopColor(stop, currentIdx, pickedUp, skipped) {
  if (stop.is_absent) return GRAY;
  if (pickedUp.has(stop.child_id)) return GREEN;
  if (skipped.has(stop.child_id)) return AMBER;
  if (stop.index === currentIdx) return ORANGE;
  return BLUE;
}

function StopMarker({ stop, currentIdx, pickedUp, skipped, onClick }) {
  const color = stopColor(stop, currentIdx, pickedUp, skipped);
  return (
    <Marker
      longitude={stop.longitude}
      latitude={stop.latitude}
      anchor="center"
    >
      <Box
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        sx={{
          width: 28, height: 28, borderRadius: '50%', background: color,
          border: '2.5px solid white', display: 'flex', alignItems: 'center',
          justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: stop.is_absent ? '0.85rem' : '0.7rem', color: 'white',
        }}
      >
        {stop.is_absent ? '✕' : stop.index + 1}
      </Box>
    </Marker>
  );
}

export default function DriverMapPage() {
  const { authTokens, backendUrl } = useContext(AuthContext);
  const { t, i18n } = useTranslation();

  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [refreshingRoutes, setRefreshingRoutes] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  // Route state
  const [routeStatus, setRouteStatus] = useState('idle'); // idle | active | stopped | finished
  const [pickedUp, setPickedUp] = useState(new Set());
  const [skipped, setSkipped] = useState(new Set());
  const [currentIdx, setCurrentIdx] = useState(0);
  const [popupStop, setPopupStop] = useState(null);
  const [snack, setSnack] = useState(null);
  const [delayDialog, setDelayDialog] = useState(false);
  const [skipDialog, setSkipDialog] = useState(false);
  const [pickupLoading, setPickupLoading] = useState(false);
  const [skipLoading, setSkipLoading] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [finishDialog, setFinishDialog] = useState(false);
  const [geoError, setGeoError] = useState(null); // null | 'denied' | 'unavailable' | 'no_support'
  const [restoringProgress, setRestoringProgress] = useState(false);
  const [driverPos, setDriverPos] = useState(null); // [lat, lng]
  const [navRouteEnabled, setNavRouteEnabled] = useState(() => localStorage.getItem('driverNavRouteEnabled') === 'true');
  const [navRouteCoords, setNavRouteCoords] = useState(null);
  const [navRouteWayPoints, setNavRouteWayPoints] = useState(null); // coord idx per stop
  const [navRouteStopOrder, setNavRouteStopOrder] = useState(null); // stop.index values in fetch order
  const [autoZoom, setAutoZoom] = useState(true);
  const [proximityAlertStop, setProximityAlertStop] = useState(null);
  const [proximityAlertEnabled, setProximityAlertEnabled] = useState(true);
  const [proximityAlertSoundEnabled, setProximityAlertSoundEnabled] = useState(true);
  const [proximityAlertRadiusM, setProximityAlertRadiusM] = useState(50);
  const [routeAbsencePopupEnabled, setRouteAbsencePopupEnabled] = useState(true);
  const [absentPopupStops, setAbsentPopupStops] = useState(null);
  const [midRouteAbsencePopup, setMidRouteAbsencePopup] = useState(null);
  const knownAbsentIdsRef = useRef(new Set());
  const [manageChildrenOpen, setManageChildrenOpen] = useState(false);
  const [pendingRemovedIds, setPendingRemovedIds] = useState(() => new Set());
  const [isTestingUser, setIsTestingUser] = useState(false);
  const [wakeLockMode, setWakeLockMode] = useState('pending'); // pending | native | fallback_video | unsupported | failed
  const [simulating, setSimulating] = useState(false);
  const [simLoading, setSimLoading] = useState(false);

  const simulatingRef = useRef(false);
  const pickedUpRef = useRef(pickedUp);
  const skippedRef = useRef(skipped);
  const watchIdRef = useRef(null);
  const posWatchRef = useRef(null);
  const posIntervalRef = useRef(null);
  const lastSentPosRef = useRef(null);
  const latestPos = useRef(null);
  const wakeLockRef = useRef(null);
  const noSleepVideoRef = useRef(null);
  const mapRef = useRef(null);
  const hasFitWithDriverRef = useRef(false);
  const navRouteFetchRouteRef = useRef(null); // route id of the last nav-route fetch
  const selectedRouteRef = useRef(null);
  const autoRestoredRef = useRef(false);
  const proximityAlertShownRef = useRef(null); // stop index for which we last triggered the proximity alert
  const proximityAlertAudioCtxRef = useRef(null);

  const authHeader = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access };

  useEffect(() => { selectedRouteRef.current = selectedRoute; }, [selectedRoute]);
  useEffect(() => { pickedUpRef.current = pickedUp; }, [pickedUp]);
  useEffect(() => { skippedRef.current = skipped; }, [skipped]);

  // Re-seed the pending-removal draft only when a *different* route becomes selected
  // (including re-selecting after navigating back) — this is what makes an uncommitted
  // removal disappear on back/reselect, while edits survive a same-route silent refresh.
  useEffect(() => {
    if (!selectedRoute) { setPendingRemovedIds(new Set()); return; }
    setPendingRemovedIds(new Set(selectedRoute.stops.filter(s => s.is_driver_removed).map(s => s.child_id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute?.id]);

  // Auto-close the stop popup after 5s if the driver doesn't dismiss it themselves.
  useEffect(() => {
    if (!popupStop) return;
    const id = setTimeout(() => setPopupStop(null), 5000);
    return () => clearTimeout(id);
  }, [popupStop]);

  useEffect(() => {
    fetch(backendUrl + 'api/driver-profile/', { headers: authHeader })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.proximityAlert_enabled === false) setProximityAlertEnabled(false);
        if (data.proximityAlertSound_enabled === false) setProximityAlertSoundEnabled(false);
        if (data.proximity_alert_radius_meters) setProximityAlertRadiusM(data.proximity_alert_radius_meters);
        if (data.routeAbsencePopup_enabled === false) setRouteAbsencePopupEnabled(false);
        if (data.is_testing_user) setIsTestingUser(true);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh the route list whenever the driver returns to it (selectedRoute → null).
  // Covers all navigation paths: back arrow, finish report, cancel route, etc.
  const hasHadRouteRef = useRef(false);
  useEffect(() => {
    if (selectedRoute !== null) {
      hasHadRouteRef.current = true;
    } else if (hasHadRouteRef.current) {
      fetchRoutes({ silent: true });
    }
  }, [selectedRoute, fetchRoutes]);

  const fetchRoutes = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingRoutes(true);
    try {
      const res = await fetch(backendUrl + 'api/driver-routes/', { headers: authHeader });
      if (res.ok) {
        const data = await res.json();
        setRoutes(data);
      }
    } catch {}
    setLoadingRoutes(false);
  }, [backendUrl, authTokens]);

  const handleRefreshRoutes = useCallback(async () => {
    setRefreshingRoutes(true);
    await fetchRoutes({ silent: true });
    setRefreshingRoutes(false);
  }, [fetchRoutes]);

  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  // On first load, auto-select the driver's active route (server-authoritative, then localStorage fallback)
  useEffect(() => {
    if (autoRestoredRef.current || loadingRoutes) return;
    autoRestoredRef.current = true;

    const myActiveRoute = routes.find(r => r.active_status === 'mine');
    if (myActiveRoute) {
      setSelectedRoute(myActiveRoute);
      setRouteStatus('idle');
      setPickedUp(new Set());
      setSkipped(new Set());
      setCurrentIdx(0);
      setGeoError(null);
      restoreRouteProgress(myActiveRoute);
      return;
    }

    const raw = localStorage.getItem('driverActiveRouteId');
    if (!raw) return;
    let routeId, ts;
    try { ({ id: routeId, ts } = JSON.parse(raw)); } catch { localStorage.removeItem('driverActiveRouteId'); return; }
    const isToday = new Date(ts).toDateString() === new Date().toDateString();
    if (!isToday) { localStorage.removeItem('driverActiveRouteId'); return; }
    const route = routes.find(r => String(r.id) === String(routeId));
    if (route) {
      setSelectedRoute(route);
      setRouteStatus('idle');
      setPickedUp(new Set());
      setSkipped(new Set());
      setCurrentIdx(0);
      setGeoError(null);
      restoreRouteProgress(route);
    } else {
      localStorage.removeItem('driverActiveRouteId');
    }
  }, [loadingRoutes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep screen awake for as long as the driver is on this page
  useEffect(() => {
    const acquire = async () => {
      if ('wakeLock' in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          setWakeLockMode('native');
          return;
        } catch {
          // Native request rejected (e.g. iOS Low Power Mode) — fall through to the video fallback.
        }
      }
      const video = ensureNoSleepVideo(noSleepVideoRef);
      if (!video) { setWakeLockMode('unsupported'); return; }
      try {
        await video.play();
        setWakeLockMode('fallback_video');
      } catch {
        setWakeLockMode('failed');
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      releaseNoSleepVideo(noSleepVideoRef);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Restore today's pickup/skip progress when a route is selected
  const restoreRouteProgress = useCallback(async (route) => {
    setRestoringProgress(true);
    // Anything already flagged absent when the route is opened is covered by the
    // start-of-route summary popup (absentPopupStops), not the mid-route one below.
    knownAbsentIdsRef.current = new Set(route.stops.filter(s => s.is_absent).map(s => s.child_id));
    try {
      const res = await fetch(backendUrl + 'api/route-stop-status/?route_id=' + route.id, {
        headers: authHeader,
      });
      if (!res.ok) return;
      const data = await res.json();
      const newPickedUp = new Set();
      const newSkipped = new Set();
      for (const s of data.stops) {
        if (s.is_picked_up) newPickedUp.add(s.child_id);
        if (s.is_skipped) newSkipped.add(s.child_id);
      }
      const done = newPickedUp.size + newSkipped.size;
      const isActive = data.driver_route_status === 'active';
      const isStopped = data.driver_route_status === 'stopped';
      if (isActive || (isStopped && done > 0)) {
        const remaining = route.stops
          .filter(s => !s.is_absent && !newPickedUp.has(s.child_id) && !newSkipped.has(s.child_id))
          .sort((a, b) => a.index - b.index);
        setPickedUp(newPickedUp);
        setSkipped(newSkipped);
        setCurrentIdx(remaining.length > 0 ? remaining[0].index : -1);
        if (isActive) {
          setRouteStatus('active');
          startPositionReporting();
        } else {
          setRouteStatus('stopped');
        }
        setSnack({ severity: 'info', msg: t('driver_map.progress_restored', { done }) });
      }
    } catch {
    } finally {
      setRestoringProgress(false);
    }
  }, [backendUrl, authTokens, startPositionReporting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Send position every 10 seconds while active
  const sendPosition = useCallback((lat, lng, speed) => {
    const route = selectedRouteRef.current;
    if (!route) return;
    const payload = { route_id: route.id, latitude: lat, longitude: lng };
    if (speed != null) payload.speed = speed;
    fetch(backendUrl + 'api/driver-position', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify(payload),
    }).catch(() => {});
  }, [backendUrl, authTokens]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNavRoute = useCallback(async (lat, lng) => {
    if (!selectedRoute || !navRouteEnabled) return;
    const orderedStops = selectedRoute.stops
      .filter(s => !s.is_absent)
      .sort((a, b) => a.index - b.index);
    if (orderedStops.length === 0) { setNavRouteCoords(null); return; }
    const waypoints = [{ lat, lng }, ...orderedStops.map(s => ({ lat: s.latitude, lng: s.longitude }))];
    try {
      const res = await fetch(backendUrl + 'api/driver-nav-route/', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ waypoints }),
      });
      if (!res.ok) {
        setSnack({ severity: 'warning', msg: t('driver_map.nav_route_unavailable') });
        return;
      }
      const data = await res.json();
      setNavRouteCoords(data.coordinates || null);
      setNavRouteWayPoints((data.way_points || []).slice(1)); // drop driver entry (always 0)
      setNavRouteStopOrder(orderedStops.map(s => s.index));
    } catch {
      setSnack({ severity: 'warning', msg: t('driver_map.nav_route_unavailable') });
    }
  }, [backendUrl, authTokens, selectedRoute, navRouteEnabled, t]);

  useEffect(() => {
    if (!navRouteEnabled) {
      setNavRouteCoords(null);
      setNavRouteWayPoints(null);
      setNavRouteStopOrder(null);
      navRouteFetchRouteRef.current = null;
      return;
    }
    if (!driverPos || navRouteFetchRouteRef.current === selectedRoute?.id) return;
    navRouteFetchRouteRef.current = selectedRoute?.id ?? null;
    const [lat, lng] = latestPos.current || driverPos;
    fetchNavRoute(lat, lng);
  }, [navRouteEnabled, driverPos, selectedRoute, fetchNavRoute]);

  // Clear proximity alert whenever the current stop changes (pickup/skip moved us on)
  useEffect(() => {
    setProximityAlertStop(null);
  }, [currentIdx]);

  // Re-arm proximity alert when language changes so it can re-trigger at the same stop
  useEffect(() => {
    proximityAlertShownRef.current = null;
  }, [i18n.language]);

  // Show proximity popup when driver is within the configured radius of the current stop
  useEffect(() => {
    if (!proximityAlertEnabled) return;
    if (routeStatus !== 'active' || !driverPos || !selectedRoute || currentIdx < 0) return;
    if (proximityAlertShownRef.current === currentIdx) return;
    const stop = selectedRoute.stops.find(s => s.index === currentIdx);
    if (!stop || stop.is_absent || pickedUp.has(stop.child_id) || skipped.has(stop.child_id)) return;
    const dist = haversineDistance(driverPos[0], driverPos[1], stop.latitude, stop.longitude);
    if (dist <= proximityAlertRadiusM) {
      proximityAlertShownRef.current = currentIdx;
      setProximityAlertStop(stop);
      if (proximityAlertSoundEnabled) playProximityAlertSound(proximityAlertAudioCtxRef);
    }
  }, [driverPos, currentIdx, routeStatus, selectedRoute, pickedUp, skipped, proximityAlertEnabled, proximityAlertRadiusM, proximityAlertSoundEnabled]);

  const startLocationWatch = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError('no_support');
      return;
    }
    if (watchIdRef.current !== null) return; // already watching
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        if (simulatingRef.current) return; // simulated position is authoritative while running
        const coords = [pos.coords.latitude, pos.coords.longitude];
        latestPos.current = coords;
        setDriverPos(coords);
        setGeoError(null);
      },
      (err) => {
        if (simulatingRef.current) return;
        setGeoError(err.code === 1 ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: true, maximumAge: 0 }
    );
  }, []);

  const startPositionReporting = useCallback(() => {
    if (!navigator.geolocation || posWatchRef.current !== null) return;
    posWatchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, speed } = pos.coords;
        if (lastSentPosRef.current) {
          const dist = haversineDistance(
            lastSentPosRef.current[0], lastSentPosRef.current[1],
            latitude, longitude
          );
          if (dist < MIN_DISTANCE_M) return;
        }
        sendPosition(latitude, longitude, speed);
        lastSentPosRef.current = [latitude, longitude];
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 0 }
    );
    // Heartbeat: send position every 30s regardless of movement so the admin
    // map doesn't drop the driver after the 2-minute stale-location cutoff.
    posIntervalRef.current = setInterval(() => {
      const pos = latestPos.current;
      if (pos) sendPosition(pos[0], pos[1], null);
    }, 30000);
  }, [sendPosition]);

  const stopLocationTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (posWatchRef.current !== null) {
      navigator.geolocation.clearWatch(posWatchRef.current);
      posWatchRef.current = null;
    }
    if (posIntervalRef.current !== null) {
      clearInterval(posIntervalRef.current);
      posIntervalRef.current = null;
    }
    lastSentPosRef.current = null;
    // Clear active route position
    fetch(backendUrl + 'api/driver-position', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify({ latitude: null, longitude: null, route_id: null }),
    }).catch(() => {});
  }, [backendUrl, authTokens]);

  // Show driver's location on map as soon as a route is selected
  useEffect(() => {
    if (selectedRoute) {
      startLocationWatch();
    } else {
      stopLocationTracking();
      setDriverPos(null);
    }
    return () => { stopLocationTracking(); };
  }, [selectedRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  const fitMapToContent = useCallback((stops, pos, schoolLat, schoolLng) => {
    if (!mapRef.current || stops.length === 0) return;
    const lngs = stops.map(s => s.longitude);
    const lats = stops.map(s => s.latitude);
    if (pos) { lngs.push(pos[1]); lats.push(pos[0]); }
    if (schoolLat != null && schoolLng != null) { lngs.push(schoolLng); lats.push(schoolLat); }
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    mapRef.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 60, duration: 600, maxZoom: 16 });
  }, []);

  const fitMapToDriverAndTarget = useCallback((pos, targetLat, targetLng) => {
    if (!mapRef.current || !pos) return;
    const lngs = [pos[1], targetLng];
    const lats = [pos[0], targetLat];
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    mapRef.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 80, duration: 400, maxZoom: 18 });
  }, []);

  // Fit to stops when a route is selected (driver pos may not be available yet)
  useEffect(() => {
    if (!selectedRoute) { hasFitWithDriverRef.current = false; return; }
    setAutoZoom(true);
    const t = setTimeout(() => {
      fitMapToContent(selectedRoute.stops, driverPos, selectedRoute.school_latitude, selectedRoute.school_longitude);
      if (driverPos) hasFitWithDriverRef.current = true;
    }, 300);
    return () => clearTimeout(t);
  }, [selectedRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fit once driver pos first becomes available (expands the bounds to include driver)
  useEffect(() => {
    if (!driverPos || !selectedRoute || hasFitWithDriverRef.current) return;
    fitMapToContent(selectedRoute.stops, driverPos, selectedRoute.school_latitude, selectedRoute.school_longitude);
    hasFitWithDriverRef.current = true;
  }, [driverPos]); // eslint-disable-line react-hooks/exhaustive-deps

  // While route is active: keep map centred on driver + next stop (or school when all done)
  useEffect(() => {
    if (routeStatus !== 'active' || !driverPos || !selectedRoute || !autoZoom) return;
    if (currentIdx === -1) {
      if (selectedRoute.route_type === 0 && selectedRoute.school_latitude && selectedRoute.school_longitude) {
        fitMapToDriverAndTarget(driverPos, selectedRoute.school_latitude, selectedRoute.school_longitude);
      }
    } else {
      const stop = selectedRoute.stops.find(s => s.index === currentIdx);
      if (stop) fitMapToDriverAndTarget(driverPos, stop.latitude, stop.longitude);
    }
  }, [routeStatus, currentIdx, driverPos, autoZoom]); // eslint-disable-line react-hooks/exhaustive-deps

  // While the route is active, periodically refresh absence status so a
  // parent-reported absence is reflected live — the stop turns into a ✕
  // marker and drops out of the remaining queue without a page reload. Any
  // child not already in knownAbsentIdsRef (i.e. cancelled after the route was
  // opened) pops the mid-route absence dialog — this is the reliability floor
  // for that dialog, independent of whether a push notification was delivered.
  useEffect(() => {
    if (routeStatus !== 'active' || !selectedRoute) return;
    const routeId = selectedRoute.id;
    const stopsSnapshot = selectedRoute.stops;
    const poll = async () => {
      try {
        const res = await fetch(backendUrl + 'api/route-stop-status/?route_id=' + routeId, { headers: authHeader });
        if (!res.ok) return;
        const data = await res.json();
        const absentIds = new Set(data.stops.filter(s => s.is_absent).map(s => s.child_id));
        const newlyAbsentIds = [...absentIds].filter(id => !knownAbsentIdsRef.current.has(id));
        absentIds.forEach(id => knownAbsentIdsRef.current.add(id));

        setSelectedRoute(prev => {
          if (!prev || prev.id !== routeId) return prev;
          let changed = false;
          const stops = prev.stops.map(s => {
            const isAbsent = absentIds.has(s.child_id);
            if (isAbsent === !!s.is_absent) return s;
            changed = true;
            return { ...s, is_absent: isAbsent };
          });
          return changed ? { ...prev, stops } : prev;
        });

        if (newlyAbsentIds.length > 0) {
          const newlyAbsentStops = stopsSnapshot.filter(s => newlyAbsentIds.includes(s.child_id));
          setMidRouteAbsencePopup(prev => (prev ? [...prev, ...newlyAbsentStops] : newlyAbsentStops));
        }
      } catch {}
    };
    const interval = setInterval(poll, STOP_STATUS_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [routeStatus, selectedRoute?.id, backendUrl, authTokens]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the driver's current stop just became a reported absence, skip it
  // automatically and move on to the next active stop. The mid-route absence
  // dialog (poll effect above) is what informs the driver now, so no snackbar here.
  useEffect(() => {
    if (routeStatus !== 'active' || !selectedRoute || currentIdx < 0) return;
    const stop = selectedRoute.stops.find(s => s.index === currentIdx);
    if (!stop || !stop.is_absent) return;
    setCurrentIdx(nextActiveStop(selectedRoute.stops, currentIdx - 1, pickedUp, skipped));
  }, [selectedRoute, currentIdx, routeStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSkip = async () => {
    if (!selectedRoute) return;
    const stop = selectedRoute.stops.find(s => s.index === currentIdx);
    if (!stop) return;
    setSkipLoading(true);
    try {
      // Testing: acting on a stop before the simulated bus has actually driven
      // there — jump the simulated position straight to the child's stop so the
      // recorded location is correct and the map reflects it immediately. The
      // running simulation loop picks up from here on its next tick.
      if (simulatingRef.current) {
        latestPos.current = [stop.latitude, stop.longitude];
        setDriverPos([stop.latitude, stop.longitude]);
        sendPosition(stop.latitude, stop.longitude, 0);
      }
      const pos = latestPos.current;
      await fetch(backendUrl + 'api/driver-skip-child/', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({
          route_id: selectedRoute.id,
          child_id: stop.child_id,
          stop_index: stop.index,
          latitude: pos ? pos[0] : null,
          longitude: pos ? pos[1] : null,
        }),
      });
      const newSkipped = new Set(skipped);
      newSkipped.add(stop.child_id);
      setSkipped(newSkipped);
      const nextIdx = nextActiveStop(selectedRoute.stops, currentIdx, pickedUp, newSkipped);
      setCurrentIdx(nextIdx);
      setPopupStop(null);
      setSnack({ severity: 'warning', msg: t('driver_map.child_skipped', { name: stop.child_firstname }) });
    } catch {
      setSnack({ severity: 'error', msg: t('driver_map.skip_failed') });
    }
    setSkipLoading(false);
    setSkipDialog(false);
  };

  const _sendRouteStartWithGps = useCallback((routeId) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, speed } = pos.coords;
        const payload = { route_id: routeId, latitude, longitude };
        if (speed != null) payload.speed = speed;
        fetch(backendUrl + 'api/driver-start-route', {
          method: 'POST',
          headers: authHeader,
          body: JSON.stringify(payload),
        }).catch(() => {});
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 },
    );
  }, [backendUrl, authTokens]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartRoute = async () => {
    unlockProximityAlertAudio(proximityAlertAudioCtxRef);
    const myActiveRoute = routes.find(r => r.active_status === 'mine');
    if (myActiveRoute && String(myActiveRoute.id) !== String(selectedRoute.id)) {
      setSnack({ severity: 'error', msg: t('driver_map.already_active_error') });
      return;
    }
    if (selectedRoute.active_status === 'other') {
      setSnack({ severity: 'error', msg: t('driver_map.route_taken_error') });
      return;
    }

    let res;
    try {
      res = await fetch(backendUrl + 'api/driver-start-route', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ route_id: selectedRoute.id, removed_child_ids: Array.from(pendingRemovedIds) }),
      });
    } catch { res = null; }

    if (res?.status === 409) {
      const data = await res.json().catch(() => ({}));
      setSnack({ severity: 'error', msg: t(data.error === 'route_taken' ? 'driver_map.route_taken_error' : 'driver_map.already_active_error') });
      fetchRoutes({ silent: true });
      return;
    }

    localStorage.setItem('driverActiveRouteId', JSON.stringify({ id: selectedRoute.id, ts: Date.now() }));
    setPickedUp(new Set());
    setSkipped(new Set());
    setCurrentIdx(firstActiveStop(selectedRoute.stops));
    setRouteStatus('active');
    startPositionReporting();
    setSnack({ severity: 'success', msg: t('driver_map.route_started') });
    _sendRouteStartWithGps(selectedRoute.id);
  };

  const handleResumeRoute = async () => {
    unlockProximityAlertAudio(proximityAlertAudioCtxRef);
    let res;
    try {
      res = await fetch(backendUrl + 'api/driver-start-route', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ route_id: selectedRoute.id }),
      });
    } catch { res = null; }

    if (res?.status === 409) {
      const data = await res.json().catch(() => ({}));
      setSnack({ severity: 'error', msg: t(data.error === 'route_taken' ? 'driver_map.route_taken_error' : 'driver_map.already_active_error') });
      fetchRoutes({ silent: true });
      return;
    }

    localStorage.setItem('driverActiveRouteId', JSON.stringify({ id: selectedRoute.id, ts: Date.now() }));
    setRouteStatus('active');
    startPositionReporting();
    setSnack({ severity: 'success', msg: t('driver_map.route_started') });
    _sendRouteStartWithGps(selectedRoute.id);
  };

  const handleCancelRoute = async () => {
    selectedRouteRef.current = null;
    stopLocationTracking();
    try {
      await fetch(backendUrl + 'api/driver-stop-route/', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ action: 'cancel', cancel_reason: cancelReason }),
      });
    } catch {}
    localStorage.removeItem('driverActiveRouteId');
    setCancelDialog(false);
    setCancelReason('');
    // Return to route list — cancel is a complete stop, not a pause
    setSelectedRoute(null);
    setRouteStatus('idle');
    setPickedUp(new Set());
    setSkipped(new Set());
    setCurrentIdx(0);
    setSnack({ severity: 'info', msg: t('driver_map.route_paused') });
  };

  const handleFinishRoute = async () => {
    // Finishing early during a simulation: halt the driving loop first so it
    // doesn't keep moving the bus (or call its own finish) after this returns.
    if (simulatingRef.current) {
      simulatingRef.current = false;
      setSimulating(false);
    }
    // Stop GPS tracking first so no in-flight pings with route_id can arrive
    // at the backend after driver-stop-route marks the route as finished.
    selectedRouteRef.current = null;
    stopLocationTracking();
    try {
      await fetch(backendUrl + 'api/driver-stop-route/', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ action: 'finish' }),
      });
    } catch {}
    localStorage.removeItem('driverActiveRouteId');
    setRouteStatus('finished');
    setSnack({ severity: 'success', msg: t('driver_map.route_finished') });
  };

  const handlePickup = async () => {
    if (!selectedRoute) return;
    const stop = selectedRoute.stops.find(s => s.index === currentIdx);
    if (!stop) return;
    setPickupLoading(true);
    try {
      // Testing: acting on a stop before the simulated bus has actually driven
      // there — jump the simulated position straight to the child's stop so the
      // recorded location is correct and the map reflects it immediately. The
      // running simulation loop picks up from here on its next tick.
      if (simulatingRef.current) {
        latestPos.current = [stop.latitude, stop.longitude];
        setDriverPos([stop.latitude, stop.longitude]);
        sendPosition(stop.latitude, stop.longitude, 0);
      }
      const pos = latestPos.current;
      await fetch(backendUrl + 'api/driver-pickup/', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({
          route_id: selectedRoute.id,
          child_id: stop.child_id,
          stop_index: stop.index,
          latitude: pos ? pos[0] : null,
          longitude: pos ? pos[1] : null,
        }),
      });
      const newPickedUp = new Set(pickedUp);
      newPickedUp.add(stop.child_id);
      setPickedUp(newPickedUp);
      const nextIdx = nextActiveStop(selectedRoute.stops, currentIdx, newPickedUp, skipped);
      setCurrentIdx(nextIdx);
      setPopupStop(null);
      setSnack({ severity: 'success', msg: t('driver_map.child_picked_up', { name: stop.child_firstname }) });
    } catch {
      setSnack({ severity: 'error', msg: t('driver_map.pickup_failed') });
    }
    setPickupLoading(false);
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleStopSimulation = async () => {
    if (!simulatingRef.current) return;
    simulatingRef.current = false;
    setSimulating(false);
    try {
      await fetch(backendUrl + 'api/driver-stop-route/', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ action: 'cancel', cancel_reason: 'Simulation stopped by tester' }),
      });
    } catch {}
    localStorage.removeItem('driverActiveRouteId');
    startLocationWatch();
    setSelectedRoute(null);
    setRouteStatus('idle');
    setPickedUp(new Set());
    setSkipped(new Set());
    setCurrentIdx(0);
    setSnack({ severity: 'info', msg: t('driver_map.simulation_stopped') });
  };

  const handleSimulateRoute = async () => {
    unlockProximityAlertAudio(proximityAlertAudioCtxRef);
    if (!selectedRoute || simulatingRef.current) return;
    const myActiveRoute = routes.find(r => r.active_status === 'mine');
    if (myActiveRoute && String(myActiveRoute.id) !== String(selectedRoute.id)) {
      setSnack({ severity: 'error', msg: t('driver_map.already_active_error') });
      return;
    }
    if (selectedRoute.active_status === 'other') {
      setSnack({ severity: 'error', msg: t('driver_map.route_taken_error') });
      return;
    }

    const orderedStops = selectedRoute.stops.filter(s => !s.is_absent).sort((a, b) => a.index - b.index);
    if (orderedStops.length === 0) {
      setSnack({ severity: 'warning', msg: t('driver_map.simulation_no_stops') });
      return;
    }

    const depotLat = selectedRoute.depot_latitude ?? selectedRoute.school_latitude;
    const depotLng = selectedRoute.depot_longitude ?? selectedRoute.school_longitude;

    setSimLoading(true);
    let navData;
    try {
      const waypoints = [{ lat: depotLat, lng: depotLng }, ...orderedStops.map(s => ({ lat: s.latitude, lng: s.longitude }))];
      const res = await fetch(backendUrl + 'api/driver-nav-route/', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ waypoints }),
      });
      if (!res.ok) {
        setSnack({ severity: 'warning', msg: t('driver_map.nav_route_unavailable') });
        setSimLoading(false);
        return;
      }
      navData = await res.json();
    } catch {
      setSnack({ severity: 'warning', msg: t('driver_map.nav_route_unavailable') });
      setSimLoading(false);
      return;
    }

    const coords = navData.coordinates;
    const wayPoints = (navData.way_points || []).slice(1);
    if (!coords || coords.length < 2 || wayPoints.length !== orderedStops.length) {
      setSnack({ severity: 'warning', msg: t('driver_map.nav_route_unavailable') });
      setSimLoading(false);
      return;
    }

    let res;
    try {
      res = await fetch(backendUrl + 'api/driver-start-route', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ route_id: selectedRoute.id, latitude: depotLat, longitude: depotLng, removed_child_ids: Array.from(pendingRemovedIds) }),
      });
    } catch { res = null; }

    if (res?.status === 409) {
      const data = await res.json().catch(() => ({}));
      setSnack({ severity: 'error', msg: t(data.error === 'route_taken' ? 'driver_map.route_taken_error' : 'driver_map.already_active_error') });
      fetchRoutes({ silent: true });
      setSimLoading(false);
      return;
    }

    setPickedUp(new Set());
    setSkipped(new Set());
    setCurrentIdx(orderedStops[0].index);
    setRouteStatus('active');
    setDriverPos([depotLat, depotLng]);
    latestPos.current = [depotLat, depotLng];
    setNavRouteCoords(coords);
    setNavRouteWayPoints(wayPoints);
    setNavRouteStopOrder(orderedStops.map(s => s.index));
    setSnack({ severity: 'success', msg: t('driver_map.simulation_started') });
    setSimLoading(false);
    simulatingRef.current = true;
    setSimulating(true);

    const SPEED_MPS = 12; // ~43 km/h simulated driving speed
    const TICK_MS = 400;
    const DWELL_MS = 10000;
    const metersPerTick = SPEED_MPS * (TICK_MS / 1000);

    let segIdx = 0;
    let segProgress = 0;
    let stopPos = 0;

    while (simulatingRef.current && stopPos < orderedStops.length) {
      const targetStop = orderedStops[stopPos];
      if (pickedUpRef.current.has(targetStop.child_id) || skippedRef.current.has(targetStop.child_id)) {
        // Tester acted on this stop early via the map popup (handlePickup/handleSkip
        // already sent the real request) — jump straight there instead of finishing
        // the drive animation, and skip the auto-pickup below so it isn't duplicated.
        latestPos.current = [targetStop.latitude, targetStop.longitude];
        setDriverPos([targetStop.latitude, targetStop.longitude]);
        sendPosition(targetStop.latitude, targetStop.longitude, 0);
        segIdx = wayPoints[stopPos];
        segProgress = 0;
        stopPos += 1;
        setCurrentIdx(stopPos < orderedStops.length ? orderedStops[stopPos].index : -1);
        await sleep(TICK_MS);
        continue;
      }
      let remaining = metersPerTick;
      while (remaining > 0 && segIdx < coords.length - 1) {
        const [lng1, lat1] = coords[segIdx];
        const [lng2, lat2] = coords[segIdx + 1];
        const segLen = haversineDistance(lat1, lng1, lat2, lng2) || 0.0001;
        const available = segLen - segProgress;
        if (remaining < available) {
          segProgress += remaining;
          remaining = 0;
        } else {
          remaining -= available;
          segIdx += 1;
          segProgress = 0;
        }
      }
      const targetSeg = Math.min(segIdx, coords.length - 2);
      const [lng1, lat1] = coords[targetSeg];
      const [lng2, lat2] = coords[targetSeg + 1];
      const segLen = haversineDistance(lat1, lng1, lat2, lng2) || 0.0001;
      const frac = Math.min(1, segProgress / segLen);
      const lat = lat1 + (lat2 - lat1) * frac;
      const lng = lng1 + (lng2 - lng1) * frac;

      latestPos.current = [lat, lng];
      setDriverPos([lat, lng]);
      sendPosition(lat, lng, SPEED_MPS * 3.6);

      const targetCoordIdx = wayPoints[stopPos];
      if (segIdx >= targetCoordIdx) {
        const stop = orderedStops[stopPos];
        await sleep(DWELL_MS);
        if (!simulatingRef.current) break;
        try {
          await fetch(backendUrl + 'api/driver-pickup/', {
            method: 'POST',
            headers: authHeader,
            body: JSON.stringify({
              route_id: selectedRoute.id,
              child_id: stop.child_id,
              stop_index: stop.index,
              latitude: lat,
              longitude: lng,
            }),
          });
          setPickedUp(prev => new Set(prev).add(stop.child_id));
          setSnack({ severity: 'success', msg: t('driver_map.child_picked_up', { name: stop.child_firstname }) });
        } catch {}
        stopPos += 1;
        setCurrentIdx(stopPos < orderedStops.length ? orderedStops[stopPos].index : -1);
        continue;
      }
      await sleep(TICK_MS);
    }

    if (simulatingRef.current) {
      await sleep(600);
      try {
        await fetch(backendUrl + 'api/driver-stop-route/', {
          method: 'POST',
          headers: authHeader,
          body: JSON.stringify({ action: 'finish' }),
        });
      } catch {}
      localStorage.removeItem('driverActiveRouteId');
      setRouteStatus('finished');
      setSnack({ severity: 'success', msg: t('driver_map.simulation_finished') });
    }
    simulatingRef.current = false;
    setSimulating(false);
    startLocationWatch();
  };

  const handleDelay = async (minutes) => {
    try {
      await fetch(backendUrl + 'api/driver-delay/', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({ delay_minutes: minutes }),
      });
      if (minutes < 0) {
        setSnack({ severity: 'success', msg: t('driver_map.early_reported', { minutes: Math.abs(minutes) }) });
      } else {
        setSnack({ severity: 'info', msg: t('driver_map.delay_reported', { minutes }) });
      }
    } catch {}
    setDelayDialog(false);
  };

  const firstActiveStop = (stops) => {
    const s = stops.find(s => !s.is_absent);
    return s ? s.index : 0;
  };

  const nextActiveStop = (stops, afterIdx, picked, skippedSet) => {
    const remaining = stops
      .filter(s => s.index > afterIdx && !s.is_absent && !picked.has(s.child_id) && !skippedSet.has(s.child_id))
      .sort((a, b) => a.index - b.index);
    return remaining.length > 0 ? remaining[0].index : -1;
  };

  const activeStops = selectedRoute ? selectedRoute.stops.filter(s => !s.is_absent) : [];
  const pickedCount = selectedRoute ? activeStops.filter(s => pickedUp.has(s.child_id) || skipped.has(s.child_id)).length : 0;
  const totalActive = activeStops.length;
  const progress = totalActive > 0 ? (pickedCount / totalActive) * 100 : 0;

  const currentStop = selectedRoute ? selectedRoute.stops.find(s => s.index === currentIdx) : null;

  const navRouteGeoJson = useMemo(() => {
    if (!navRouteCoords || !navRouteWayPoints || !navRouteStopOrder || currentIdx < 0) return null;
    const pos = navRouteStopOrder.indexOf(currentIdx);
    if (pos === -1) return null;
    const segStart = pos === 0 ? 0 : navRouteWayPoints[pos - 1];
    const segEnd = navRouteWayPoints[pos];
    if (segStart == null || segEnd == null) return null;
    const sliced = navRouteCoords.slice(segStart, segEnd + 1);
    if (sliced.length < 2) return null;
    return { type: 'Feature', geometry: { type: 'LineString', coordinates: sliced }, properties: {} };
  }, [navRouteCoords, navRouteWayPoints, navRouteStopOrder, currentIdx]);

  if (loadingRoutes) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: AMBER }} />
      </Box>
    );
  }

  if (!selectedRoute) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 3, pb: 'calc(24px + var(--sab))', pl: 'calc(24px + var(--sal))', pr: 'calc(24px + var(--sar))' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.4rem', color: NAV_BG, flex: 1 }}>
            {t('nav.driver_map')}
          </Typography>
          <IconButton onClick={handleRefreshRoutes} disabled={refreshingRoutes} size="small" sx={{ color: NAV_BG }}>
            <RefreshIcon sx={{ fontSize: 22, animation: refreshingRoutes ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
          </IconButton>
        </Box>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', mb: 3 }}>
          {t('driver_map.select_route')}
        </Typography>
        {routes.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>{t('driver_map.no_routes')}</Alert>
        ) : (() => {
          const myActiveRoute = routes.find(r => r.active_status === 'mine');
          return (
            <List disablePadding>
              {routes.map((route) => {
                const isMyActive = route.active_status === 'mine';
                const isOtherActive = route.active_status === 'other';
                const isLocked = isOtherActive || (myActiveRoute && !isMyActive);
                return (
                  <Paper
                    key={route.id}
                    onClick={() => {
                      if (isLocked) return;
                      setSelectedRoute(route);
                      setRouteStatus('idle');
                      setPickedUp(new Set());
                      setSkipped(new Set());
                      setCurrentIdx(0);
                      setGeoError(null);
                      restoreRouteProgress(route);
                      if (routeAbsencePopupEnabled) {
                        const absentStops = route.stops.filter(s => s.is_absent);
                        if (absentStops.length > 0) setAbsentPopupStops(absentStops);
                      }
                    }}
                    sx={{
                      mb: 1.5, p: 2, borderRadius: 2,
                      cursor: isLocked ? 'default' : 'pointer',
                      border: isMyActive ? `1px solid ${GREEN}` : '1px solid #e2e8f0',
                      opacity: isLocked ? 0.6 : 1,
                      ...(!isLocked && { '&:hover': { borderColor: AMBER, boxShadow: `0 0 0 1px ${AMBER}` } }),
                      transition: 'all 0.15s',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <RouteIcon sx={{ color: isMyActive ? GREEN : AMBER, fontSize: 22 }} />
                      <Box>
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: NAV_BG }}>
                          {route.name}
                        </Typography>
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#64748b' }}>
                          {(() => {
                            const total = route.stops.length;
                            const active = route.stops.filter(s => !s.is_absent).length;
                            return active < total
                              ? t('driver_map.stops_active', { count: total, active })
                              : t('driver_map.stops_count', { count: total });
                          })()}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {isMyActive && (
                          <Chip size="small" label={t('driver_map.route_active_badge')}
                            sx={{ background: '#dcfce7', color: '#16a34a', fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 700 }} />
                        )}
                        {isOtherActive && (
                          <Chip size="small" label={t('driver_map.route_in_use_badge')}
                            sx={{ background: '#fef3c7', color: '#b45309', fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 700 }} />
                        )}
                        <Chip
                          size="small"
                          label={route.route_type === 0 ? t('driver_map.to_school') : route.route_type === 1 ? t('driver_map.from_school') : t('driver_map.route_label')}
                          sx={{ background: '#eff6ff', color: '#1d4ed8', fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem' }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </List>
          );
        })()}
      </Box>
    );
  }

  // Route completion report — shown instead of the map after finishing
  if (routeStatus === 'finished') {
    const isDropOff = selectedRoute.route_type === 1;
    const allStops = [...selectedRoute.stops].sort((a, b) => a.index - b.index);
    const pickedUpCount = allStops.filter(s => !s.is_absent && pickedUp.has(s.child_id)).length;
    const skippedCount = allStops.filter(s => !s.is_absent && skipped.has(s.child_id)).length;
    const notReachedCount = allStops.filter(s => !s.is_absent && !pickedUp.has(s.child_id) && !skipped.has(s.child_id)).length;
    const absentCount = allStops.filter(s => s.is_absent).length;
    const goBackToList = () => {
      stopLocationTracking();
      setSelectedRoute(null);
      setRouteStatus('idle');
      setPickedUp(new Set());
      setSkipped(new Set());
      setCurrentIdx(0);
    };
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 2.5, pb: 'calc(20px + var(--sab))', pl: 'calc(20px + var(--sal))', pr: 'calc(20px + var(--sar))', overflowY: 'auto' }}>
        <Box sx={{ textAlign: 'center', pt: 3, pb: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: GREEN }} />
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '1.5rem', color: NAV_BG, mt: 1 }}>
            {t('driver_map.route_complete_title')}
          </Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.92rem', color: '#64748b', mt: 0.5 }}>
            {selectedRoute.name}
          </Typography>
          <Chip
            size="small"
            label={selectedRoute.route_type === 0 ? t('driver_map.to_school') : selectedRoute.route_type === 1 ? t('driver_map.from_school') : t('driver_map.route_label')}
            sx={{ mt: 1, background: '#eff6ff', color: '#1d4ed8', fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem' }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3, flexWrap: 'wrap' }}>
          <Chip label={`✓  ${pickedUpCount}`} sx={{ background: '#dcfce7', color: '#16a34a', fontWeight: 700, fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }} />
          {skippedCount > 0 && <Chip label={`⏭  ${skippedCount}`} sx={{ background: '#fef3c7', color: '#b45309', fontWeight: 700, fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }} />}
          {notReachedCount > 0 && <Chip label={`—  ${notReachedCount}`} sx={{ background: '#fee2e2', color: '#dc2626', fontWeight: 700, fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }} />}
          {absentCount > 0 && <Chip label={`✕  ${absentCount}`} sx={{ background: '#f1f5f9', color: '#94a3b8', fontWeight: 700, fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem' }} />}
        </Box>

        <List disablePadding sx={{ mb: 3 }}>
          {allStops.map(stop => {
            let statusColor, statusBg, statusLabel;
            if (stop.is_absent) {
              statusColor = '#94a3b8'; statusBg = '#f1f5f9'; statusLabel = t('driver_map.report_absent');
            } else if (pickedUp.has(stop.child_id)) {
              statusColor = '#16a34a'; statusBg = '#dcfce7'; statusLabel = isDropOff ? t('driver_map.report_dropped_off') : t('driver_map.report_picked_up');
            } else if (skipped.has(stop.child_id)) {
              statusColor = '#b45309'; statusBg = '#fef3c7'; statusLabel = t('driver_map.report_skipped');
            } else {
              statusColor = '#dc2626'; statusBg = '#fee2e2'; statusLabel = t('driver_map.report_not_reached');
            }
            return (
              <Paper key={stop.child_id} sx={{ mb: 1, p: 1.5, borderRadius: 1.5, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: statusBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.72rem', color: statusColor }}>
                    {stop.index + 1}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.9rem', color: NAV_BG }}>
                    {stop.child_firstname} {stop.child_lastname}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {stop.name}
                  </Typography>
                </Box>
                <Chip size="small" label={statusLabel} sx={{ background: statusBg, color: statusColor, fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem', fontWeight: 600, flexShrink: 0 }} />
              </Paper>
            );
          })}
        </List>

        <Button
          fullWidth variant="contained" disableElevation onClick={goBackToList}
          sx={{ background: NAV_BG, color: 'white', fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', borderRadius: 1.5, py: 1.4, '&:hover': { background: '#1e3a8a' } }}
        >
          {t('driver_map.back_to_routes')}
        </Button>
      </Box>
    );
  }

  // Map view for selected route
  const mapCenter = selectedRoute.stops.length > 0
    ? { lng: selectedRoute.stops[0].longitude, lat: selectedRoute.stops[0].latitude }
    : { lng: 23.7, lat: 37.97 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 64px - var(--sat) - var(--sab))', overflow: 'hidden' }}>
      {/* Top bar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
        background: NAV_BG, color: 'white', zIndex: 10,
      }}>
        <IconButton onClick={() => { stopLocationTracking(); setSelectedRoute(null); setRouteStatus('idle'); }} sx={{ color: 'white', p: 0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1rem' }}>
            {selectedRoute.name}
          </Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)' }}>
            {t('driver_map.picked_count', { count: pickedCount, total: totalActive })}
          </Typography>
        </Box>
        {routeStatus === 'active' && (
          <Button
            size="small"
            onClick={() => setDelayDialog(true)}
            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: AMBER, borderColor: AMBER, border: '1px solid', borderRadius: 1, px: 1, py: 0.3, textTransform: 'none' }}
          >
            <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} />
            {t('driver_map.delay_btn')}
          </Button>
        )}
        {selectedRoute.stops.some(s => s.is_absent) && (
          <IconButton
            size="small"
            onClick={() => setAbsentPopupStops(selectedRoute.stops.filter(s => s.is_absent))}
            title={t('driver_map.view_absences_btn')}
            sx={{ color: 'rgba(255,255,255,0.85)', p: 0.5 }}
          >
            <PersonOffIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* Restoring progress indicator */}
      {restoringProgress && (
        <LinearProgress sx={{ height: 3, background: 'rgba(59,130,246,0.15)', '& .MuiLinearProgress-bar': { background: '#3b82f6' } }} />
      )}

      {/* Geolocation error banner */}
      {geoError === 'denied' ? (
        <Alert
          severity="error"
          sx={{ borderRadius: 0, fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', py: 0.75 }}
          action={
            <Button
              size="small"
              onClick={() => window.location.reload()}
              sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', textTransform: 'none', color: 'inherit', whiteSpace: 'nowrap', alignSelf: 'flex-start', mt: 0.25 }}
            >
              {t('driver_map.geo_reload')}
            </Button>
          }
        >
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.3 }}>
            {t('driver_map.geo_denied')}
          </Typography>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', mt: 0.3, lineHeight: 1.4 }}>
            {t(`driver_map.geo_fix_${detectBrowser()}`)}
          </Typography>
        </Alert>
      ) : geoError ? (
        <Alert severity="error" sx={{ borderRadius: 0, fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', py: 0.5 }}>
          {t(`driver_map.geo_${geoError}`)}
        </Alert>
      ) : null}

      {/* Progress bar */}
      {routeStatus === 'active' && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 4, background: 'rgba(245,158,11,0.2)', '& .MuiLinearProgress-bar': { background: AMBER } }}
        />
      )}

      {/* Map */}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }} onTouchEnd={() => document.activeElement?.blur()}>
        <Map
          ref={mapRef}
          initialViewState={{ longitude: mapCenter.lng, latitude: mapCenter.lat, zoom: 13 }}
          style={{ width: '100%', height: '100%' }}
          mapStyle={MAP_STYLE}
          keyboard={false}
          onMoveStart={(e) => { if (e.originalEvent) setAutoZoom(false); }}
        >
          {navRouteGeoJson && (
            <Source id="nav-route" type="geojson" data={navRouteGeoJson}>
              <Layer
                id="nav-route-line"
                type="line"
                layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': '#3b82f6', 'line-width': 2, 'line-opacity': 0.7 }}
              />
            </Source>
          )}
          {selectedRoute.stops.map((stop) => (
            <StopMarker
              key={stop.child_id}
              stop={stop}
              currentIdx={currentIdx}
              pickedUp={pickedUp}
              skipped={skipped}
              onClick={() => setPopupStop(stop)}
            />
          ))}
          {selectedRoute.school_latitude && selectedRoute.school_longitude && (
            <Marker longitude={selectedRoute.school_longitude} latitude={selectedRoute.school_latitude} anchor="bottom">
              <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'default',
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '50%', background: '#7c3aed',
                  border: '3px solid white', boxShadow: '0 2px 10px rgba(124,58,237,0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem',
                }}>
                  🏫
                </Box>
              </Box>
            </Marker>
          )}
          {driverPos && (
            <Marker longitude={driverPos[1]} latitude={driverPos[0]} anchor="center">
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{
                  position: 'absolute',
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'rgba(59,130,246,0.2)',
                  animation: 'driverPulse 2s ease-out infinite',
                  '@keyframes driverPulse': {
                    '0%': { transform: 'scale(0.7)', opacity: 1 },
                    '100%': { transform: 'scale(1.4)', opacity: 0 },
                  },
                }} />
                <Box sx={{
                  width: 40, height: 40, borderRadius: '50%', background: BLUE,
                  border: '3px solid white',
                  boxShadow: '0 2px 12px rgba(59,130,246,0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1, fontSize: '1.1rem',
                }}>
                  🚌
                </Box>
              </Box>
            </Marker>
          )}
          {popupStop && (
            <Popup
              longitude={popupStop.longitude}
              latitude={popupStop.latitude}
              anchor="bottom"
              onClose={() => setPopupStop(null)}
              closeButton={true}
            >
              <Box sx={{ p: 1, minWidth: 160 }}>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.9rem', color: NAV_BG }}>
                  {popupStop.child_firstname} {popupStop.child_lastname}
                </Typography>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#64748b', mb: 1 }}>
                  {popupStop.address || popupStop.name}
                </Typography>
                {popupStop.is_absent ? (
                  <Chip size="small" label={t('driver_map.absent')} sx={{ background: '#fee2e2', color: RED, fontSize: '0.7rem' }} />
                ) : pickedUp.has(popupStop.child_id) ? (
                  <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={t('driver_map.status_picked_up')} sx={{ background: '#dcfce7', color: GREEN, fontSize: '0.7rem' }} />
                ) : skipped.has(popupStop.child_id) ? (
                  <Chip size="small" label={t('driver_map.status_skipped')} sx={{ background: '#fffbeb', color: AMBER_DARK, fontSize: '0.7rem' }} />
                ) : routeStatus === 'active' && popupStop.index === currentIdx ? (
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <Button
                      size="small" variant="contained" disableElevation
                      onClick={handlePickup}
                      disabled={pickupLoading}
                      sx={{ background: GREEN, color: 'white', fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', textTransform: 'none', borderRadius: 1 }}
                    >
                      {pickupLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : selectedRoute?.route_type === 1 ? t('driver_map.dropped_off_btn') : t('driver_map.picked_up_btn')}
                    </Button>
                    <Button
                      size="small" variant="outlined" disableElevation
                      onClick={() => { setPopupStop(null); setSkipDialog(true); }}
                      sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', textTransform: 'none', borderRadius: 1, borderColor: AMBER, color: AMBER_DARK }}
                    >
                      {t('driver_map.skip_btn')}
                    </Button>
                  </Box>
                ) : (
                  <Chip size="small" label={t('driver_map.upcoming')} sx={{ background: '#dbeafe', color: BLUE, fontSize: '0.7rem' }} />
                )}
              </Box>
            </Popup>
          )}
        </Map>
        {/* Proximity alert — appears when driver is within the configured radius of the current stop */}
        {routeStatus === 'active' && proximityAlertStop && (
          <Box sx={{
            position: 'absolute', top: 8, left: 8, right: 8, zIndex: 20,
            background: 'white', borderRadius: 2, border: `2px solid ${GREEN}`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)', p: 1.5,
          }}>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.78rem', color: GREEN, mb: 0.25 }}>
              {t('driver_map.arrived_at_stop')}
            </Typography>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.95rem', color: NAV_BG }}>
              {proximityAlertStop.child_firstname} {proximityAlertStop.child_lastname}
            </Typography>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#64748b', mb: 1 }}>
              {proximityAlertStop.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained" disableElevation size="small"
                onClick={() => { setProximityAlertStop(null); handlePickup(); }}
                disabled={pickupLoading}
                sx={{ flex: 2, background: GREEN, color: 'white', fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', borderRadius: 1.5 }}
              >
                {pickupLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : selectedRoute?.route_type === 1 ? t('driver_map.dropped_off_btn') : t('driver_map.picked_up_btn')}
              </Button>
              <Button
                variant="outlined" disableElevation size="small"
                onClick={() => { setProximityAlertStop(null); setSkipDialog(true); }}
                sx={{ flex: 1, fontFamily: '"Outfit", sans-serif', textTransform: 'none', borderRadius: 1.5, borderColor: AMBER, color: AMBER_DARK }}
              >
                {t('driver_map.skip_btn')}
              </Button>
              <Button
                variant="outlined" disableElevation size="small"
                onClick={() => setProximityAlertStop(null)}
                sx={{ flex: 1, fontFamily: '"Outfit", sans-serif', textTransform: 'none', borderRadius: 1.5, borderColor: '#e2e8f0', color: '#64748b' }}
              >
                {t('driver_map.dismiss')}
              </Button>
            </Box>
          </Box>
        )}
        {/* Re-center button — visible when driver has panned/zoomed away */}
        {!autoZoom && (
          <Box sx={{ position: 'absolute', bottom: 56, right: 12, zIndex: 10 }}>
            <Box
              component="button"
              onClick={() => {
                setAutoZoom(true);
              }}
              title={t('driver_map.recenter')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                px: 1.25, py: 0.75, borderRadius: 2, border: 'none', cursor: 'pointer',
                background: AMBER, color: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', fontWeight: 600,
              }}
            >
              <MyLocationIcon sx={{ fontSize: 16 }} />
              {t('driver_map.recenter')}
            </Box>
          </Box>
        )}
        {/* Navigation route toggle */}
        <Box sx={{ position: 'absolute', bottom: 12, right: 12, zIndex: 10 }}>
          <Box
            component="button"
            onClick={() => {
              const next = !navRouteEnabled;
              setNavRouteEnabled(next);
              localStorage.setItem('driverNavRouteEnabled', String(next));
              if (!next) {
                setNavRouteCoords(null);
              } else if (!driverPos) {
                setSnack({ severity: 'warning', msg: t('driver_map.nav_route_no_location') });
              }
            }}
            title={t('driver_map.nav_route_toggle')}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              px: 1.25, py: 0.75, borderRadius: 2, border: 'none', cursor: 'pointer',
              background: navRouteEnabled ? BLUE : 'rgba(255,255,255,0.92)',
              color: navRouteEnabled ? '#fff' : '#475569',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', fontWeight: 600,
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            <RouteIcon sx={{ fontSize: 16 }} />
            {t('driver_map.nav_route_toggle')}
          </Box>
        </Box>
      </Box>

      {/* Bottom action panel — in flow so it's always fully visible */}
      <Paper sx={{
        borderRadius: 0,
        borderTop: '1px solid #e2e8f0',
        p: 2,
        pb: 'calc(16px + var(--sab))',
        pl: 'calc(16px + var(--sal))',
        pr: 'calc(16px + var(--sar))',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
        flexShrink: 0,
      }}>
        {routeStatus === 'idle' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {currentStop && !currentStop.is_absent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: ORANGE, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#64748b' }}>
                  {t('driver_map.first_stop')}: <strong>{currentStop.child_firstname}</strong> — {currentStop.name}
                </Typography>
              </Box>
            )}
            <Button
              fullWidth variant="outlined" disableElevation
              onClick={() => setManageChildrenOpen(true)}
              startIcon={<GroupIcon />}
              sx={{
                fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none',
                borderRadius: 1.5, py: 1, borderColor: '#cbd5e1', color: NAV_BG,
                '&:hover': { borderColor: AMBER, background: 'rgba(217,119,6,0.06)' },
              }}
            >
              {t('driver_map.manage_children_btn')}
              {pendingRemovedIds.size > 0 && (
                <Chip
                  size="small" label={pendingRemovedIds.size}
                  sx={{ ml: 1, height: 20, fontSize: '0.72rem', fontWeight: 700, background: '#fee2e2', color: '#dc2626' }}
                />
              )}
            </Button>
            <Button
              fullWidth variant="contained" disableElevation
              onClick={handleStartRoute}
              startIcon={<PlayArrowIcon />}
              sx={{
                background: GREEN, color: 'white', fontFamily: '"Outfit", sans-serif',
                fontWeight: 700, textTransform: 'none', borderRadius: 1.5,
                py: 1.2, '&:hover': { background: '#059669' },
              }}
            >
              {t('driver_map.start_route')}
            </Button>
            {isTestingUser && (
              <Button
                fullWidth variant="outlined" disableElevation
                onClick={handleSimulateRoute}
                disabled={simLoading}
                startIcon={simLoading ? <CircularProgress size={16} sx={{ color: '#9c27b0' }} /> : <ScienceIcon />}
                sx={{
                  fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none',
                  borderRadius: 1.5, py: 1.1, borderColor: '#9c27b0', color: '#9c27b0',
                  '&:hover': { background: 'rgba(156,39,176,0.06)', borderColor: '#9c27b0' },
                }}
              >
                {t('driver_map.simulate_route')}
              </Button>
            )}
            {isTestingUser && (
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#94a3b8', textAlign: 'center' }}>
                {t(`driver_map.wake_lock_${wakeLockMode}`)}
              </Typography>
            )}
          </Box>
        )}

        {routeStatus === 'active' && simulating && (
          <Box>
            <Box sx={{ mb: 1.5, p: 1.5, background: '#faf5ff', borderRadius: 1.5, border: '1px solid #e9d5ff' }}>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#7e22ce', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ScienceIcon sx={{ fontSize: 16 }} /> {t('driver_map.simulation_running')}
              </Typography>
              {currentStop && (
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#6b21a8', mt: 0.5 }}>
                  {t('driver_map.next_stop')}: <strong>{currentStop.child_firstname} {currentStop.child_lastname}</strong>
                </Typography>
              )}
              {currentStop && currentIdx >= 0 && (
                <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
                  <Button
                    size="small" variant="contained" disableElevation
                    onClick={handlePickup}
                    disabled={pickupLoading}
                    sx={{ background: GREEN, color: 'white', fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.78rem', textTransform: 'none', borderRadius: 1 }}
                  >
                    {pickupLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : selectedRoute?.route_type === 1 ? t('driver_map.dropped_off_btn') : t('driver_map.picked_up_btn')}
                  </Button>
                  <Button
                    size="small" variant="outlined" disableElevation
                    onClick={() => setSkipDialog(true)}
                    disabled={skipLoading}
                    sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.78rem', textTransform: 'none', borderRadius: 1, borderColor: AMBER, color: AMBER_DARK }}
                  >
                    {t('driver_map.skip_btn')}
                  </Button>
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined" disableElevation onClick={handleStopSimulation}
                startIcon={<StopIcon />}
                sx={{ flex: 1, fontFamily: '"Outfit", sans-serif', textTransform: 'none', borderRadius: 1.5, py: 1, borderColor: '#9c27b0', color: '#9c27b0' }}
              >
                {t('driver_map.stop_simulation')}
              </Button>
              <Button
                variant="contained" disableElevation
                onClick={() => {
                  const pending = activeStops.filter(s => !pickedUp.has(s.child_id) && !skipped.has(s.child_id));
                  if (pending.length > 0) {
                    setFinishDialog(true);
                  } else {
                    handleFinishRoute();
                  }
                }}
                sx={{
                  flex: 1, background: AMBER_DARK, color: NAV_BG, fontFamily: '"Outfit", sans-serif',
                  fontWeight: 700, textTransform: 'none', borderRadius: 1.5, py: 1,
                  '&:hover': { background: AMBER },
                }}
              >
                {t('driver_map.finish_route')}
              </Button>
            </Box>
          </Box>
        )}

        {routeStatus === 'active' && !simulating && (
          <Box>
            {currentStop && !pickedUp.has(currentStop?.child_id) && !skipped.has(currentStop?.child_id) && currentIdx >= 0 && (
              <Box sx={{ mb: 1.5, p: 1.5, background: '#fffbeb', borderRadius: 1.5, border: '1px solid #fde68a' }}>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#92400e', fontWeight: 600 }}>
                  {elUpperCase(t('driver_map.next_stop'))}
                </Typography>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', color: '#78350f', fontWeight: 700 }}>
                  {currentStop.child_firstname} {currentStop.child_lastname}
                </Typography>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#92400e' }}>
                  {currentStop.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    variant="contained" disableElevation
                    onClick={() => { setPopupStop(null); handlePickup(); }}
                    disabled={pickupLoading}
                    sx={{
                      flex: 2, background: GREEN, color: 'white', fontFamily: '"Outfit", sans-serif',
                      fontWeight: 700, textTransform: 'none', borderRadius: 1.5, py: 0.8,
                      '&:hover': { background: '#059669' },
                    }}
                  >
                    {pickupLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : selectedRoute?.route_type === 1 ? t('driver_map.dropped_off_btn') : t('driver_map.picked_up_btn')}
                  </Button>
                  <Button
                    variant="outlined" disableElevation
                    onClick={() => setSkipDialog(true)}
                    disabled={skipLoading}
                    sx={{
                      flex: 1, fontFamily: '"Outfit", sans-serif', fontWeight: 700,
                      textTransform: 'none', borderRadius: 1.5, py: 0.8,
                      borderColor: AMBER, color: AMBER_DARK,
                      '&:hover': { background: 'rgba(245,158,11,0.06)', borderColor: AMBER },
                    }}
                  >
                    {t('driver_map.skip_btn')}
                  </Button>
                </Box>
              </Box>
            )}
            {currentIdx === -1 && (
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: GREEN, fontWeight: 600, mb: 1 }}>
                {t('driver_map.all_done')}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined" disableElevation onClick={() => setCancelDialog(true)}
                startIcon={<StopIcon />}
                sx={{ flex: 1, fontFamily: '"Outfit", sans-serif', textTransform: 'none', borderRadius: 1.5, py: 1, borderColor: '#e2e8f0', color: '#64748b' }}
              >
                {t('driver_map.pause')}
              </Button>
              <Button
                variant="contained" disableElevation
                onClick={() => {
                  const pending = activeStops.filter(s => !pickedUp.has(s.child_id) && !skipped.has(s.child_id));
                  if (pending.length > 0) {
                    setFinishDialog(true);
                  } else {
                    handleFinishRoute();
                  }
                }}
                sx={{
                  flex: 2, background: AMBER_DARK, color: NAV_BG, fontFamily: '"Outfit", sans-serif',
                  fontWeight: 700, textTransform: 'none', borderRadius: 1.5, py: 1,
                  '&:hover': { background: AMBER },
                }}
              >
                {t('driver_map.finish_route')}
              </Button>
            </Box>
          </Box>
        )}

        {(routeStatus === 'stopped' || routeStatus === 'finished') && (
          <Box>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: NAV_BG, mb: 1 }}>
              {routeStatus === 'finished' ? t('driver_map.status_finished') : t('driver_map.status_paused')}
              {' '}— {t('driver_map.picked_count', { count: pickedCount, total: totalActive })}
            </Typography>
            {routeStatus === 'stopped' && (
              <Button
                fullWidth variant="contained" disableElevation onClick={handleResumeRoute}
                startIcon={<PlayArrowIcon />}
                sx={{
                  background: GREEN, color: 'white', fontFamily: '"Outfit", sans-serif',
                  fontWeight: 700, textTransform: 'none', borderRadius: 1.5, py: 1.2,
                  '&:hover': { background: '#059669' },
                }}
              >
                {t('driver_map.resume_route')}
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* Absent children popup */}
      <Dialog open={!!absentPopupStops} onClose={() => setAbsentPopupStops(null)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700 }}>{t('driver_map.absent_popup_title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#64748b', mb: 1.5 }}>
            {t('driver_map.absent_popup_subtitle', { count: absentPopupStops?.length || 0 })}
          </Typography>
          <List disablePadding>
            {(absentPopupStops || []).map(stop => (
              <ListItem key={stop.child_id} disableGutters sx={{ py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <PersonOffIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.88rem', color: NAV_BG }}>{stop.child_firstname} {stop.child_lastname}</Typography>}
                  secondary={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#64748b' }}>{stop.address || stop.name}</Typography>}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbsentPopupStops(null)} sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', color: NAV_BG }}>{t('common.ok')}</Button>
        </DialogActions>
      </Dialog>

      {/* Mid-route absence popup — a parent cancelled a child after the route was already open */}
      <Dialog open={!!midRouteAbsencePopup} onClose={() => setMidRouteAbsencePopup(null)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700 }}>{t('driver_map.mid_route_absence_title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#64748b', mb: 1.5 }}>
            {t('driver_map.mid_route_absence_subtitle', { count: midRouteAbsencePopup?.length || 0 })}
          </Typography>
          <List disablePadding>
            {(midRouteAbsencePopup || []).map(stop => (
              <ListItem key={stop.child_id} disableGutters sx={{ py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <WarningAmberIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.88rem', color: NAV_BG }}>{stop.child_firstname} {stop.child_lastname}</Typography>}
                  secondary={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#64748b' }}>{stop.address || stop.name}</Typography>}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMidRouteAbsencePopup(null)} sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', color: NAV_BG }}>{t('common.ok')}</Button>
        </DialogActions>
      </Dialog>

      {/* Manage children on this route (pre-start only) */}
      <Dialog open={manageChildrenOpen} onClose={() => setManageChildrenOpen(false)} PaperProps={{ sx: { borderRadius: 2 } }} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700 }}>{t('driver_map.manage_children_title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.82rem', color: '#64748b', mb: 1.5 }}>
            {t('driver_map.manage_children_subtitle')}
          </Typography>
          <List disablePadding>
            {[...(selectedRoute?.stops || [])].sort((a, b) => a.index - b.index).map(stop => {
              const isPendingRemoved = pendingRemovedIds.has(stop.child_id);
              return (
                <ListItem key={stop.child_id} disableGutters sx={{ py: 0.75, borderBottom: '1px solid #f1f5f9', gap: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {stop.is_parent_absent ? (
                      <PersonOffIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                    ) : (
                      <PersonRemoveIcon sx={{ fontSize: 18, color: isPendingRemoved ? '#dc2626' : '#94a3b8' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.86rem', color: NAV_BG }}>{stop.child_firstname} {stop.child_lastname}</Typography>}
                    secondary={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.76rem', color: '#64748b' }}>{stop.address || stop.name}</Typography>}
                  />
                  {stop.is_parent_absent ? (
                    <Chip size="small" label={t('driver_map.absent')} sx={{ background: '#f1f5f9', color: '#94a3b8', fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.7rem' }} />
                  ) : isPendingRemoved ? (
                    <Button
                      size="small" variant="outlined" startIcon={<UndoIcon sx={{ fontSize: 15 }} />}
                      onClick={() => setPendingRemovedIds(prev => { const s = new Set(prev); s.delete(stop.child_id); return s; })}
                      sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', fontSize: '0.74rem', borderColor: GREEN, color: GREEN, flexShrink: 0 }}
                    >
                      {t('driver_map.undo_remove_btn')}
                    </Button>
                  ) : (
                    <Button
                      size="small" variant="outlined" startIcon={<PersonRemoveIcon sx={{ fontSize: 15 }} />}
                      onClick={() => setPendingRemovedIds(prev => new Set(prev).add(stop.child_id))}
                      sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', fontSize: '0.74rem', borderColor: '#dc2626', color: '#dc2626', flexShrink: 0 }}
                    >
                      {t('driver_map.remove_child_btn')}
                    </Button>
                  )}
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageChildrenOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', color: NAV_BG }}>{t('common.ok')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delay / Early Arrival dialog */}
      <Dialog open={delayDialog} onClose={() => setDelayDialog(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700 }}>{t('driver_map.report_delay_title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', fontWeight: 600, color: '#64748b', mb: 0.75 }}>
            {t('driver_map.delay_section')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            {[5, 10, 15].map(min => (
              <Button
                key={min} variant="outlined" onClick={() => handleDelay(min)}
                sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, borderRadius: 1.5, textTransform: 'none', flex: 1, borderColor: AMBER, color: AMBER_DARK }}
              >
                +{min} {t('driver_map.min')}
              </Button>
            ))}
          </Box>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', fontWeight: 600, color: '#64748b', mb: 0.75 }}>
            {t('driver_map.early_section')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {[5, 10, 15].map(min => (
              <Button
                key={min} variant="outlined" onClick={() => handleDelay(-min)}
                sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, borderRadius: 1.5, textTransform: 'none', flex: 1, borderColor: GREEN, color: GREEN }}
              >
                -{min} {t('driver_map.min')}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelayDialog(false)} sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', textTransform: 'none' }}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel route dialog */}
      <Dialog open={cancelDialog} onClose={() => { setCancelDialog(false); setCancelReason(''); }} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700 }}>{t('driver_map.cancel_route_title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
            {['traffic', 'breakdown', 'driver', 'other'].map(key => (
              <Button
                key={key}
                variant={cancelReason === t(`driver_map.cancel_reason_${key}`) ? 'contained' : 'outlined'}
                disableElevation
                onClick={() => setCancelReason(r => r === t(`driver_map.cancel_reason_${key}`) ? '' : t(`driver_map.cancel_reason_${key}`))}
                sx={{
                  fontFamily: '"Outfit", sans-serif', textTransform: 'none', borderRadius: 1.5, justifyContent: 'flex-start',
                  ...(cancelReason === t(`driver_map.cancel_reason_${key}`)
                    ? { background: '#0f2d5e', color: 'white' }
                    : { borderColor: '#e2e8f0', color: '#374151' }),
                }}
              >
                {t(`driver_map.cancel_reason_${key}`)}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCancelDialog(false); setCancelReason(''); }} sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button
            onClick={handleCancelRoute}
            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', color: '#dc2626' }}
          >
            {t('driver_map.confirm_cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Finish with pending children confirmation dialog */}
      <Dialog open={finishDialog} onClose={() => setFinishDialog(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700 }}>{t('driver_map.finish_pending_title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b' }}>
            {t('driver_map.finish_pending_msg', {
              count: activeStops.filter(s => !pickedUp.has(s.child_id) && !skipped.has(s.child_id)).length,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinishDialog(false)} sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button
            onClick={() => { setFinishDialog(false); handleFinishRoute(); }}
            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', color: AMBER_DARK }}
          >
            {t('driver_map.finish_pending_confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Skip confirmation dialog */}
      <Dialog open={skipDialog} onClose={() => setSkipDialog(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700 }}>{t('driver_map.skip_confirm_title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b' }}>
            {currentStop ? t('driver_map.skip_confirm_msg', { name: `${currentStop.child_firstname} ${currentStop.child_lastname}` }) : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipDialog(false)} sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSkip}
            disabled={skipLoading}
            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, textTransform: 'none', color: AMBER_DARK }}
          >
            {skipLoading ? <CircularProgress size={16} sx={{ color: AMBER_DARK }} /> : t('driver_map.skip_btn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={20000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 'calc(8px + var(--sat))', sm: 'calc(24px + var(--sat))' } }}
      >
        <Alert severity={snack?.severity || 'info'} onClose={() => setSnack(null)} sx={{ borderRadius: 2 }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
